import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { formatISO } from 'date-fns';
import { IdNamePair } from '../../types/globalTypes';
import {
  WarehouseAssetType,
  WarehouseLeadTimesSettingsAPI,
  WarehouseLocationBasicDetails,
} from '../../types/warehouseTypes';

interface AssetCache extends IdNamePair {
  assetType: WarehouseAssetType;
}

interface LocationCache extends WarehouseLocationBasicDetails {
  warehouseId: number | string;
}

interface LeadTimesSettingsCacheEntry {
  data: WarehouseLeadTimesSettingsAPI;
  fetchedOn: string;
}

interface WarehouseState {
  basicWarehouses: Array<IdNamePair>;
  basicTerminals: Array<IdNamePair>;
  assets: Record<string, AssetCache>;
  locations: Record<string, LocationCache>;
  lastFetchedWarehousesOn: string | null;
  lastFetchedTerminalsOn: string | null;
  lastFetchedAssetsOn: string | null;
  lastFetchedLocationsOn: string | null;
  selectedWarehouseId: number | null;
  leadTimesSettingsCache: Record<string, LeadTimesSettingsCacheEntry>;
}

type CachedType = 'basicWarehouses' | 'basicTerminals' | 'assets' | 'locations';
type AssetResult = {
  input: string;
  data: AssetCache;
};

type LocationResult = {
  cacheKey: string;
  data: LocationCache;
};

const initialState: WarehouseState = {
  basicWarehouses: [],
  basicTerminals: [],
  assets: {},
  locations: {},
  lastFetchedTerminalsOn: null,
  lastFetchedWarehousesOn: null,
  lastFetchedAssetsOn: null,
  lastFetchedLocationsOn: null,
  selectedWarehouseId: null,
  leadTimesSettingsCache: {},
};

const warehouses = createSlice({
  name: 'warehouses',
  initialState,
  reducers: {
    getWarehouses(state, action: PayloadAction<Array<IdNamePair>>) {
      state.basicWarehouses = action.payload;
      state.lastFetchedWarehousesOn = formatISO(new Date());
    },
    getTerminals(state, action: PayloadAction<Array<IdNamePair>>) {
      state.basicTerminals = action.payload;
      state.lastFetchedTerminalsOn = formatISO(new Date());
    },
    getAsset(state, action: PayloadAction<AssetResult>) {
      state.assets[action.payload.input] = action.payload.data;
      state.lastFetchedAssetsOn = formatISO(new Date());
    },
    getLocation(state, action: PayloadAction<LocationResult>) {
      state.locations[action.payload.cacheKey] = action.payload.data;
      state.lastFetchedLocationsOn = formatISO(new Date());
    },
    invalidateWarehouseCache(state, action: PayloadAction<CachedType>) {
      switch (action.payload) {
        case 'basicWarehouses':
          state.lastFetchedWarehousesOn = null;
          break;
        case 'basicTerminals':
          state.lastFetchedTerminalsOn = null;
          break;
        case 'assets':
          state.lastFetchedAssetsOn = null;
          break;
        case 'locations':
          state.lastFetchedLocationsOn = null;
          break;
      }
    },
    setSelectedWarehouse(state, action: PayloadAction<number>) {
      state.selectedWarehouseId = action.payload;
    },
    clearSelectedWarehouse(state) {
      state.selectedWarehouseId = null;
    },
    setLeadTimesSettingsCache(
      state,
      action: PayloadAction<{
        warehouseId: string;
        data: WarehouseLeadTimesSettingsAPI;
      }>,
    ) {
      state.leadTimesSettingsCache[action.payload.warehouseId] = {
        data: action.payload.data,
        fetchedOn: formatISO(new Date()),
      };
    },
    invalidateLeadTimesSettingsCache(
      state,
      action: PayloadAction<string | number>,
    ) {
      delete state.leadTimesSettingsCache[String(action.payload)];
    },
  },
});

export const {
  getWarehouses,
  getTerminals,
  getAsset,
  getLocation,
  invalidateWarehouseCache,
  setSelectedWarehouse,
  clearSelectedWarehouse,
  setLeadTimesSettingsCache,
  invalidateLeadTimesSettingsCache,
} = warehouses.actions;

export const WarehousesReducer = warehouses.reducer;
